import { globalVFS } from '@/lib/vfs/VFS';
import { generateEmbedding384 } from '@/lib/embeddings';
import { isRuVectorAvailable, ruvectorSearch } from '@/lib/ruvectorClient';
import { normalizeWinPath } from '@/lib/agentAccess';

type FileMetadata = {
  path: string;
  type: 'file' | 'folder';
  size: number;
  modified: Date | string;
  content?: string;
};

export type GraphNodeType = 'folder' | 'file' | 'chat' | 'project';

export type GraphNodeInfo = {
  id: string;
  type: GraphNodeType;
  path: string;
  name: string;
  size?: number;
  modifiedMs?: number;
};

export type GraphEdgeType = 'containment' | 'semantic' | 'activity';

export type GraphEdgeInfo = {
  id: string;
  type: GraphEdgeType;
  source: string;
  target: string;
  weight?: number;
  label?: string;
};

const INDEXABLE_EXTS = new Set([
  'txt',
  'md',
  'csv',
  'json',
  'js',
  'ts',
  'tsx',
  'css',
  'html',
  'log',
  'ini',
  'conf',
]);

export const FILE_SIZE_CAP_BYTES = 1_000_000; // 1MB
export const GRAPH_MAX_FILES = 100;

export function isIndexableExt(path: string): boolean {
  const name = path.split('\\').pop() || path;
  const ext = name.includes('.') ? name.split('.').pop()!.toLowerCase() : '';
  return INDEXABLE_EXTS.has(ext);
}

function makeId(prefix: string, path: string) {
  return `${prefix}:${normalizeWinPath(path)}`;
}

async function getMetadata(path: string): Promise<FileMetadata | null> {
  const all = await globalVFS.getAllFiles();
  const match = all.find((f: any) => normalizeWinPath(f.path) === normalizeWinPath(path));
  if (!match) return null;

  return {
    path: match.path,
    type: match.type,
    size: match.size,
    modified: match.modified,
    content: match.content,
  } as any;
}

async function readText(path: string): Promise<string | null> {
  return await globalVFS.load(path);
}

async function listDir(path: string): Promise<string[]> {
  return await globalVFS.list(path);
}

export type FolderTree = {
  rootPath: string;
  nodes: GraphNodeInfo[];
  containmentEdges: GraphEdgeInfo[];
  filesForSemantic: GraphNodeInfo[];
};

export async function listFolderTree(rootPath: string, opts?: { maxFiles?: number }): Promise<FolderTree> {
  const root = normalizeWinPath(rootPath);
  const maxFiles = opts?.maxFiles ?? GRAPH_MAX_FILES;

  const exists = await globalVFS.exists(root);
  if (!exists) {
    return { rootPath: root, nodes: [], containmentEdges: [], filesForSemantic: [] };
  }

  const nodes: GraphNodeInfo[] = [];
  const edges: GraphEdgeInfo[] = [];
  const filesForSemantic: GraphNodeInfo[] = [];

  const seen = new Set<string>();
  const queue: string[] = [root];

  const pushNode = async (path: string, typeHint?: GraphNodeType) => {
    const p = normalizeWinPath(path);
    const id = makeId('node', p);
    if (seen.has(id)) return id;
    seen.add(id);

    const meta = await getMetadata(p);
    const metaType = meta?.type === 'folder' ? 'folder' : meta?.type === 'file' ? 'file' : undefined;

    const name = p.split('\\').pop() || p;
    const type: GraphNodeType = typeHint || (metaType as GraphNodeType) || 'file';
    const info: GraphNodeInfo = {
      id,
      type,
      path: p,
      name,
      size: typeof meta?.size === 'number' ? meta.size : undefined,
      modifiedMs: meta?.modified ? new Date(meta.modified as any).getTime() : undefined,
    };
    nodes.push(info);

    if (type === 'file' && isIndexableExt(p) && (info.size ?? 0) <= FILE_SIZE_CAP_BYTES) {
      filesForSemantic.push(info);
    }

    return id;
  };

  await pushNode(root, 'folder');

  let fileCount = 0;

  while (queue.length > 0) {
    const dir = queue.shift()!;

    const children = await listDir(dir);
    for (const child of children) {
      const meta = await getMetadata(child);
      const isFolder = meta?.type === 'folder';
      const isFile = meta?.type === 'file';

      if (isFile) {
        if (!isIndexableExt(child)) continue;
        if (typeof meta?.size === 'number' && meta.size > FILE_SIZE_CAP_BYTES) continue;
        fileCount += 1;
        if (fileCount > maxFiles) continue;
      }

      const parentId = makeId('node', normalizeWinPath(dir));
      const childId = await pushNode(child, isFolder ? 'folder' : isFile ? 'file' : undefined);

      edges.push({
        id: makeId('edge:contain', `${parentId}=>${childId}`),
        type: 'containment',
        source: parentId,
        target: childId,
      });

      if (isFolder) queue.push(child);
    }
  }

  return { rootPath: root, nodes, containmentEdges: edges, filesForSemantic };
}

export async function buildActivityEdges(docsPath: string): Promise<{ nodes: GraphNodeInfo[]; edges: GraphEdgeInfo[] }> {
  const docs = normalizeWinPath(docsPath);
  const chatsRoot = `${docs}\\Chats`;
  const projectsRoot = `${docs}\\Projects`;

  const nodes: GraphNodeInfo[] = [];
  const edges: GraphEdgeInfo[] = [];
  const nodeByPath = new Map<string, GraphNodeInfo>();

  const ensureNode = async (path: string, type: GraphNodeType) => {
    const p = normalizeWinPath(path);
    if (nodeByPath.has(p)) return nodeByPath.get(p)!;

    const meta = await getMetadata(p);
    const info: GraphNodeInfo = {
      id: makeId('node', p),
      type,
      path: p,
      name: p.split('\\').pop() || p,
      size: typeof meta?.size === 'number' ? meta.size : undefined,
      modifiedMs: meta?.modified ? new Date(meta.modified as any).getTime() : undefined,
    };
    nodeByPath.set(p, info);
    nodes.push(info);
    return info;
  };

  const scanChatFolder = async (chatFolder: string) => {
    const metaPath = `${chatFolder}\\meta.json`;
    const accessMarker = `${chatFolder}\\.os-access.txt`;

    const hasMeta = await globalVFS.exists(metaPath);
    const hasMarker = await globalVFS.exists(accessMarker);
    if (!hasMeta && !hasMarker) return;

    const chatNode = await ensureNode(chatFolder, 'chat');

    const attachmentsDir = `${chatFolder}\\Attachments`;
    if (await globalVFS.exists(attachmentsDir)) {
      const attNode = await ensureNode(attachmentsDir, 'folder');
      edges.push({
        id: makeId('edge:activity', `${chatNode.id}=>${attNode.id}`),
        type: 'activity',
        source: chatNode.id,
        target: attNode.id,
      });

      const attFiles = await globalVFS.list(attachmentsDir);
      for (const f of attFiles) {
        const m = await getMetadata(f);
        if (m?.type !== 'file') continue;
        const fileNode = await ensureNode(f, 'file');
        edges.push({
          id: makeId('edge:activity', `${attNode.id}=>${fileNode.id}`),
          type: 'activity',
          source: attNode.id,
          target: fileNode.id,
        });
      }
    }

    // If chat is under a project folder, create a chat <-> project activity edge.
    const chatNorm = normalizeWinPath(chatFolder);
    if (chatNorm.startsWith(normalizeWinPath(projectsRoot) + '\\')) {
      // project folder = immediate child of Projects
      const rest = chatNorm.substring(normalizeWinPath(projectsRoot).length + 1);
      const projectName = rest.split('\\')[0];
      const projectFolder = `${projectsRoot}\\${projectName}`;
      const projectNode = await ensureNode(projectFolder, 'project');
      edges.push({
        id: makeId('edge:activity', `${chatNode.id}=>${projectNode.id}`),
        type: 'activity',
        source: chatNode.id,
        target: projectNode.id,
      });
    }
  };

  const scanProjects = async () => {
    if (!(await globalVFS.exists(projectsRoot))) return;
    const projectFolders = await globalVFS.list(projectsRoot);
    for (const p of projectFolders) {
      const m = await getMetadata(p);
      if (m?.type !== 'folder') continue;

      // project node if it looks like an enabled project
      const hasMeta = await globalVFS.exists(`${p}\\meta.json`);
      const hasReadme = await globalVFS.exists(`${p}\\README.txt`);
      if (hasMeta || hasReadme) {
        await ensureNode(p, 'project');
      }

      // scan immediate child folders for chats
      const children = await globalVFS.list(p);
      for (const c of children) {
        const cm = await getMetadata(c);
        if (cm?.type !== 'folder') continue;
        await scanChatFolder(c);
      }
    }
  };

  const scanChats = async () => {
    if (!(await globalVFS.exists(chatsRoot))) return;
    const chatFolders = await globalVFS.list(chatsRoot);
    for (const c of chatFolders) {
      const cm = await getMetadata(c);
      if (cm?.type !== 'folder') continue;
      await scanChatFolder(c);
    }
  };

  await scanProjects();
  await scanChats();

  return { nodes, edges };
}

export async function buildSemanticEdges(
  files: GraphNodeInfo[],
  topN: number,
  threshold: number
): Promise<GraphEdgeInfo[]> {
  if (!isRuVectorAvailable()) return [];

  const cap = Math.min(files.length, GRAPH_MAX_FILES);
  const sample = files.slice(0, cap);

  const pathToNodeId = new Map(sample.map((f) => [normalizeWinPath(f.path), f.id]));
  const edges: GraphEdgeInfo[] = [];

  for (const f of sample) {
    // eslint-disable-next-line no-await-in-loop
    const text = await readText(f.path);
    if (!text) continue;

    // eslint-disable-next-line no-await-in-loop
    const emb = await generateEmbedding384(text.slice(0, 15_000));
    if (!emb) continue;

    // eslint-disable-next-line no-await-in-loop
    const res = await ruvectorSearch({ source: 'os', queryEmbedding: emb, limit: Math.max(1, Math.min(5, topN)) });
    if (!res.ok) continue;

    for (const r of res.results) {
      const p = r.path ? normalizeWinPath(r.path) : '';
      const targetId = p ? pathToNodeId.get(p) : undefined;
      if (!targetId) continue;
      const sim = typeof r.similarity === 'number' ? r.similarity : 0;
      if (sim < threshold) continue;
      if (targetId === f.id) continue;

      edges.push({
        id: makeId('edge:semantic', `${f.id}=>${targetId}`),
        type: 'semantic',
        source: f.id,
        target: targetId,
        weight: sim,
        label: sim.toFixed(2),
      });
    }
  }

  // de-dupe
  const seen = new Set<string>();
  return edges.filter((e) => {
    const key = `${e.source}|${e.target}|${e.type}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}